import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import type { Department } from '../types';
import { Upload, Send, Clock, CheckCircle, XCircle, AlertCircle, Eye, RefreshCw, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type TabType = 'create' | 'list';

export const BulkDispatchesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('create');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDispatches, setIsLoadingDispatches] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importedContacts, setImportedContacts] = useState<any[]>([]);
  const [ignoredContacts, setIgnoredContacts] = useState<any[]>([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState<any>(null);
  const [showDispatchDetail, setShowDispatchDetail] = useState(false);
  const [showImportedContactsModal, setShowImportedContactsModal] = useState(false);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsPerPage] = useState(20);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    templateName: '',
    departmentId: '',
    scheduledAt: '',
    sendNow: true,
  });

  // Filtros para lista de disparos
  const [filters, setFilters] = useState({
    searchName: '',
    status: '',
    templateName: '',
  });

  // Pagination for dispatches list
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadDepartments();
    loadTemplates();
    if (activeTab === 'list') {
      loadDispatches();
    }
  }, [activeTab]);

  const loadDepartments = async () => {
    try {
      const departments = await api.getDepartments();
      setDepartments(departments);

      const financeiro = departments.find((d: Department) => d.name === 'Financeiro');
      if (financeiro && !formData.departmentId) {
        setFormData(prev => ({ ...prev, departmentId: financeiro.id }));
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const response = await api.getTemplates();
      const approvedTemplates = response.data.filter((t: any) => t.status === 'APPROVED');
      setTemplates(approvedTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadDispatches = async () => {
    try {
      setIsLoadingDispatches(true);
      const response = await api.getBulkDispatches();
      setDispatches(response.dispatches || []);
    } catch (error) {
      console.error('Error loading dispatches:', error);
    } finally {
      setIsLoadingDispatches(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setError(null);

    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);

          const validContacts: any[] = [];
          const invalidContacts: any[] = [];

          jsonData.forEach((row: any, index: number) => {
            const rowNum = index + 2;

            if (!row.nome || !row.telefone) {
              invalidContacts.push({
                linha: rowNum,
                nome: row.nome,
                telefone: row.telefone,
                motivo: 'Nome ou telefone faltando'
              });
              return;
            }

            let cleanPhone = String(row.telefone).replace(/\D/g, '');
            let normalizedPhone: string | null = null;

            if (cleanPhone.startsWith('55')) {
              if (cleanPhone.length === 13) {
                normalizedPhone = cleanPhone;
              } else if (cleanPhone.length === 12) {
                const areaCode = cleanPhone.substring(2, 4);
                const number = cleanPhone.substring(4);
                normalizedPhone = `55${areaCode}9${number}`;
              }
            } else if (cleanPhone.length === 11) {
              normalizedPhone = `55${cleanPhone}`;
            } else if (cleanPhone.length === 10) {
              const areaCode = cleanPhone.substring(0, 2);
              const number = cleanPhone.substring(2);
              normalizedPhone = `55${areaCode}9${number}`;
            }

            if (!normalizedPhone || normalizedPhone.length !== 13) {
              invalidContacts.push({
                linha: rowNum,
                nome: row.nome,
                telefone: row.telefone,
                motivo: `Formato de telefone inválido (${cleanPhone.length} dígitos após limpeza)`
              });
              return;
            }

            const contactData: any = {
              nome: row.nome,
              telefone: normalizedPhone,
            };

            if (row.dias_cancelamento) {
              contactData.customVariables = { dias_cancelamento: row.dias_cancelamento };
            }

            validContacts.push(contactData);
          });

          setImportedContacts(validContacts);
          setIgnoredContacts(invalidContacts);

          if (validContacts.length > 0) {
            setImportSuccess(true);
            setSuccess(`✅ ${validContacts.length} contato(s) válido(s) importado(s)${invalidContacts.length > 0 ? ` | ⚠️ ${invalidContacts.length} ignorado(s)` : ''}`);
          } else {
            setError('Nenhum contato válido encontrado na planilha');
            setImportSuccess(false);
          }
        } catch (error) {
          console.error('Error parsing spreadsheet:', error);
          setError('Erro ao processar planilha. Verifique o formato do arquivo.');
          setImportSuccess(false);
        } finally {
          setIsImporting(false);
        }
      };

      reader.readAsBinaryString(file);
    } catch (error) {
      console.error('Error reading file:', error);
      setError('Erro ao ler arquivo');
      setIsImporting(false);
    }
  };

  const exportToExcel = async (dispatch: any) => {
    try {
      const XLSX = await import('xlsx');

      // Prepare data for export
      const exportData = dispatch.contacts.map((contact: any) => ({
        'Nome': contact.name,
        'Telefone': contact.phone,
        'Status': contact.status,
        'Enviado em': contact.sentAt ? new Date(contact.sentAt).toLocaleString('pt-BR') : '',
        'Resposta': contact.responseType === 'YES' ? 'Sim' : contact.responseType === 'NO' ? 'Não' : contact.responseType || '',
        'Respondido em': contact.responseAt ? new Date(contact.responseAt).toLocaleString('pt-BR') : '',
        'Variáveis': contact.customVariables || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Contatos');

      // Generate filename with dispatch name and date
      const filename = `disparo_${dispatch.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Erro ao exportar planilha');
    }
  };

  // Filter dispatches
  const filteredDispatches = dispatches.filter((dispatch) => {
    if (filters.searchName && !dispatch.name.toLowerCase().includes(filters.searchName.toLowerCase())) {
      return false;
    }
    if (filters.status && dispatch.status !== filters.status) {
      return false;
    }
    if (filters.templateName && dispatch.templateName !== filters.templateName) {
      return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredDispatches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDispatches = filteredDispatches.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!importSuccess || importedContacts.length === 0) {
      setError('Importe uma planilha válida antes de criar o disparo');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let scheduledAt;
      if (formData.sendNow) {
        scheduledAt = new Date().toISOString();
      } else {
        scheduledAt = new Date(formData.scheduledAt).toISOString();
      }

      const payload = {
        name: formData.name,
        templateName: formData.templateName,
        departmentId: formData.departmentId,
        scheduledAt,
        sendNow: formData.sendNow,
        contacts: importedContacts.map(contact => ({
          name: contact.nome,
          phone: contact.telefone,
          customVariables: contact.customVariables ? JSON.stringify(contact.customVariables) : null,
        })),
      };

      await api.createBulkDispatch(payload);
      setShowSuccessModal(true);
      setSuccess('Disparo criado com sucesso!');

      setFormData({
        name: '',
        templateName: '',
        departmentId: departments.find(d => d.name === 'Financeiro')?.id || '',
        scheduledAt: '',
        sendNow: true,
      });
      setImportedContacts([]);
      setIgnoredContacts([]);
      setImportSuccess(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error creating bulk dispatch:', error);
      setShowErrorModal(true);
      setError(error.response?.data?.error || 'Erro ao criar disparo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewDispatchDetails = async (dispatchId: string) => {
    try {
      const response = await api.getBulkDispatch(dispatchId);
      setSelectedDispatch(response.dispatch);
      setShowDispatchDetail(true);
      setContactsPage(1);
    } catch (error) {
      console.error('Error loading dispatch details:', error);
    }
  };

  const cancelDispatch = async (dispatchId: string) => {
    if (!confirm('Tem certeza que deseja cancelar este disparo?')) return;

    try {
      await api.cancelBulkDispatch(dispatchId);
      loadDispatches();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Erro ao cancelar disparo');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <Clock className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
      case 'IN_PROGRESS': return <Send className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case 'COMPLETED': return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'CANCELLED': return <XCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />;
      case 'FAILED': return <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      default: return null;
    }
  };

  const getContactStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <Clock className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />;
      case 'SENT': return <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400" />;
      case 'FAILED': return <XCircle className="w-3 h-3 text-red-600 dark:text-red-400" />;
      case 'RESPONDED_YES': return <CheckCircle className="w-3 h-3 text-blue-600 dark:text-blue-400" />;
      case 'RESPONDED_NO': return <XCircle className="w-3 h-3 text-orange-600 dark:text-orange-400" />;
      case 'NO_RESPONSE': return <AlertCircle className="w-3 h-3 text-gray-600 dark:text-gray-400" />;
      default: return null;
    }
  };

  const paginatedContacts = selectedDispatch?.contacts.slice(
    (contactsPage - 1) * contactsPerPage,
    contactsPage * contactsPerPage
  );

  const totalContactPages = selectedDispatch ? Math.ceil(selectedDispatch.contacts.length / contactsPerPage) : 0;

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Send className="w-6 h-6" />
          Disparos em Massa
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Gerencie envios em massa de templates WhatsApp
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('create')}
            className={`px-4 py-3 font-medium border-b-2 transition ${
              activeTab === 'create'
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            📤 Iniciar Disparo
          </button>
          <button
            onClick={() => {
              setActiveTab('list');
              loadDispatches();
            }}
            className={`px-4 py-3 font-medium border-b-2 transition ${
              activeTab === 'list'
                ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            📋 Ver Disparos
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Tab: Iniciar Disparo */}
        {activeTab === 'create' && (
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome do Disparo
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Template WhatsApp
                </label>
                <select
                  value={formData.templateName}
                  onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um template aprovado</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.name}>
                      {template.name}
                    </option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">
                    Nenhum template aprovado encontrado. Crie e aprove templates primeiro.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Departamento
                </label>
                <select
                  value={formData.departmentId}
                  onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um departamento</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Quando enviar?
                </label>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.sendNow}
                      onChange={() => setFormData({ ...formData, sendNow: true, scheduledAt: '' })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      🚀 Enviar imediatamente
                    </span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.sendNow}
                      onChange={() => setFormData({ ...formData, sendNow: false })}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      📅 Agendar para data/hora específica
                    </span>
                  </label>

                  {!formData.sendNow && (
                    <div className="ml-6 mt-2">
                      <input
                        type="datetime-local"
                        value={formData.scheduledAt}
                        onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        O disparo será processado automaticamente a cada 5 minutos
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Planilha de Contatos
                </label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    {isImporting ? 'Importando...' : 'Selecionar Arquivo'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.ods,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={isImporting}
                    />
                  </label>
                  {importSuccess && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowImportedContactsModal(true)}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Ver {importedContacts.length} contatos
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setImportedContacts([]);
                          setIgnoredContacts([]);
                          setImportSuccess(false);
                          setSuccess(null);
                          if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                          }
                        }}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                      >
                        Substituir planilha
                      </button>
                    </>
                  )}
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Formatos aceitos:
                  </p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 ml-4">
                    <li>• Planilhas: .xlsx, .xls, .ods, .csv</li>
                    <li>• Colunas obrigatórias: "nome" e "telefone"</li>
                    <li>• Coluna opcional: "dias_cancelamento" (para variáveis customizadas)</li>
                  </ul>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-2">
                    Formatos de telefone aceitos:
                  </p>
                  <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 ml-4">
                    <li>• Com DDD 9 dígitos: 34 99999-9999 ou 34999999999</li>
                    <li>• Com DDD 8 dígitos: 34 8888-9999 ou 3488889999 (adiciona 9 automaticamente)</li>
                    <li>• Com código país: 5534999999999 ou 553488889999</li>
                    <li>• Formato internacional: +55 34 99999-9999</li>
                  </ul>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
                    ⚠️ Telefones em formato inválido serão ignorados e listados abaixo
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting || !importSuccess}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-600 dark:disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Criando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Criar Disparo
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab: Ver Disparos */}
        {activeTab === 'list' && (
          <div className="max-w-7xl mx-auto">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Disparos Criados
                  </h3>
                  <button
                    onClick={loadDispatches}
                    disabled={isLoadingDispatches}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                  >
                    <RefreshCw className={`w-4 h-4 ${isLoadingDispatches ? 'animate-spin' : ''}`} />
                    Atualizar
                  </button>
                </div>

                {/* Filtros */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Buscar por nome..."
                    value={filters.searchName}
                    onChange={(e) => setFilters(prev => ({ ...prev, searchName: e.target.value }))}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos os status</option>
                    <option value="PENDING">PENDING</option>
                    <option value="PROCESSING">PROCESSING</option>
                    <option value="COMPLETED">COMPLETED</option>
                    <option value="FAILED">FAILED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </select>
                  <select
                    value={filters.templateName}
                    onChange={(e) => setFilters(prev => ({ ...prev, templateName: e.target.value }))}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos os templates</option>
                    {templates.map((template: any) => (
                      <option key={template.name} value={template.name}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {isLoadingDispatches ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600" />
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Carregando disparos...</p>
                </div>
              ) : filteredDispatches.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {dispatches.length === 0 ? 'Nenhum disparo encontrado' : 'Nenhum disparo corresponde aos filtros'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nome</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Template</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Progresso</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Agendado</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedDispatches.map((dispatch) => (
                        <tr key={dispatch.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{dispatch.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{dispatch.templateName}</td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-1 text-sm font-medium ${
                              dispatch.status === 'PENDING' ? 'text-yellow-600 dark:text-yellow-400' :
                              dispatch.status === 'IN_PROGRESS' ? 'text-blue-600 dark:text-blue-400' :
                              dispatch.status === 'COMPLETED' ? 'text-green-600 dark:text-green-400' :
                              dispatch.status === 'CANCELLED' ? 'text-gray-600 dark:text-gray-400' :
                              dispatch.status === 'FAILED' ? 'text-red-600 dark:text-red-400' :
                              'text-gray-900 dark:text-white'
                            }`}>
                              {getStatusIcon(dispatch.status)}
                              {dispatch.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {dispatch.sentCount || 0} / {dispatch.totalContacts}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {format(new Date(dispatch.scheduledAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => viewDispatchDetails(dispatch.id)}
                                className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                                title="Ver detalhes"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {dispatch.status === 'PENDING' && (
                                <button
                                  onClick={() => cancelDispatch(dispatch.id)}
                                  className="p-1 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded"
                                  title="Cancelar"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  {filteredDispatches.length > 0 && (
                    <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Mostrando {startIndex + 1} a {Math.min(endIndex, filteredDispatches.length)} de {filteredDispatches.length} registros
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Página {currentPage} de {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Próxima
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sucesso!</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{success}</p>
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Erro</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => setShowErrorModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Dispatch Detail Modal */}
      {showDispatchDetail && selectedDispatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Detalhes do Disparo
              </h3>
              <button
                onClick={() => setShowDispatchDetail(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Nome</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedDispatch.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Template</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{selectedDispatch.templateName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="mt-1 flex items-center gap-1">
                    {getStatusIcon(selectedDispatch.status)}
                    <span className="text-sm text-gray-900 dark:text-white">{selectedDispatch.status}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Progresso</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedDispatch.sentCount || 0} / {selectedDispatch.totalContacts}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  Contatos ({selectedDispatch.contacts.length})
                </h4>
                <button
                  onClick={() => exportToExcel(selectedDispatch)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Exportar Planilha
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nome</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Telefone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Enviado em</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Resposta</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedContacts.map((contact: any) => (
                      <tr key={contact.id}>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{contact.name}</td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{contact.phone}</td>
                        <td className="px-3 py-2">
                          <span className="flex items-center gap-1">
                            {getContactStatusIcon(contact.status)}
                            <span className="text-gray-600 dark:text-gray-400">{contact.status}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                          {contact.sentAt ? new Date(contact.sentAt).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="px-3 py-2">
                          {contact.responseType && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              contact.responseType === 'YES'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : contact.responseType === 'NO'
                                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                            }`}>
                              {contact.responseType === 'YES' ? '✓ Sim' : contact.responseType === 'NO' ? '✗ Não' : contact.responseType}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalContactPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    onClick={() => setContactsPage(Math.max(1, contactsPage - 1))}
                    disabled={contactsPage === 1}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Página {contactsPage} de {totalContactPages}
                  </span>
                  <button
                    onClick={() => setContactsPage(Math.min(totalContactPages, contactsPage + 1))}
                    disabled={contactsPage === totalContactPages}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Imported Contacts Modal */}
      {showImportedContactsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Contatos Importados
              </h3>
              <button
                onClick={() => setShowImportedContactsModal(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Contatos Válidos */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Contatos Válidos ({importedContacts.length})
                </h4>
                <div className="overflow-x-auto max-h-60 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Nome</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Telefone</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Variáveis</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {importedContacts.map((contact, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{idx + 1}</td>
                          <td className="px-3 py-2 text-gray-900 dark:text-white">{contact.nome}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 font-mono text-xs">{contact.telefone}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-400 text-xs">
                            {contact.customVariables ? JSON.stringify(contact.customVariables) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Contatos Ignorados */}
              {ignoredContacts.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Contatos Ignorados ({ignoredContacts.length})
                  </h4>
                  <div className="overflow-x-auto max-h-60 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-yellow-50 dark:bg-yellow-900/20 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 dark:text-yellow-400 uppercase">Linha</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 dark:text-yellow-400 uppercase">Nome</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 dark:text-yellow-400 uppercase">Telefone</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-yellow-700 dark:text-yellow-400 uppercase">Motivo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-yellow-200 dark:divide-yellow-800">
                        {ignoredContacts.map((contact, idx) => (
                          <tr key={idx} className="hover:bg-yellow-50 dark:hover:bg-yellow-900/10">
                            <td className="px-3 py-2 text-yellow-800 dark:text-yellow-300">{contact.linha}</td>
                            <td className="px-3 py-2 text-yellow-800 dark:text-yellow-300">{contact.nome || '-'}</td>
                            <td className="px-3 py-2 text-yellow-800 dark:text-yellow-300 font-mono text-xs">{contact.telefone || '-'}</td>
                            <td className="px-3 py-2 text-yellow-700 dark:text-yellow-400 text-xs">{contact.motivo}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setShowImportedContactsModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
